import Layout from "@/components/Layout";
import Seo from "@/components/seo";
import { useTranslation, I18nContext } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import React, { useState, useContext, useEffect } from "react";
import CoinzillaHorizontal from "@/components/adBanners/CoinzillaHorizontal";
import { GuideArticleWrapper, StyledImageWrapper } from "@/styles/style";
import GuideHeader from "@/components/guidePostCommons/guideHeader";
import { checkChosenExchange } from "@/utils/utils";
import Image from "next/image";
import styled from "styled-components";
import theme from "@/styles/theme";
import CtaLink from "@/components/ctaLink";
import ReactMarkdown from "react-markdown";
import remarkUnlink from "remark-unlink";
import rehypeRaw from "rehype-raw";
import { elRenderer } from "@/utils/utils";
import { fetchTranslations } from "@/utils/utils";

const StyledCounter = styled.span`
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${theme.accent_3};
  border-radius: 50%;
  width: 1.4em;
  height: 1.4em;
  color: ${theme.white};
  margin-right: 0.5em;
`;

const { defaultExchange } = require("@/config/siteConfig");

export default function CoinDetailsPage({ data: { attributes } }) {
  const { t } = useTranslation("newguide");
  const langContext = useContext(I18nContext);
  const [post, setPost] = useState(attributes);
  const [chosenExchange, setChosenExchange] = useState(defaultExchange);
  const title = t("articleTitle", { name: post.name, symbol: post.symbol });
  const origCmcId = post.cmc_id.toString().split("-")[0];
  const imgUrl = `${process.env.CMC_STATIC_URL}static/img/coins/200x200/${origCmcId}.png`;
  const exchangeTxt = t(`exchanges.${chosenExchange}`);
  const step2Sequence = [
    t(`step2Content.p1`, { name: post.name, symbol: post.symbol }),
    t(`step2Content.card_title`, { name: post.name }),
    t(`step2Content.card_paragraph`, { name: post.name }),
    t(`step2Content.card_list`, { name: post.name, exchange: exchangeTxt }),
    t(`step2Content.bank_title`, { name: post.name }),
    t(`step2Content.bank_list`, { name: post.name, exchange: exchangeTxt }),
    t(`step2Content.other_title`),
    t(`step2Content.other_paragraph`, {
      name: post.name,
      exchange: exchangeTxt,
    }),
  ];
  useEffect(() => {
    if (checkChosenExchange(post).length > 0) {
      setChosenExchange(checkChosenExchange(post)[0]);
    }
  }, [post]);
  return (
    <Layout title={title} noSidebar>
      <Seo
        title={t("articleTitle", { name: post.name, symbol: post.symbol })}
        langContext={langContext}
        coin={post}
        description={t("articleTitle", {
          name: post.name,
          symbol: post.symbol,
        })}
      />
      <article itemScope itemType="https://schema.org/HowTo">
        <StyledImageWrapper width={`56px`} height={`56px`}>
          <Image
            unoptimized
            width={48}
            height={48}
            src={imgUrl}
            alt={post.name}
          />
        </StyledImageWrapper>
        <GuideHeader title={title} time={post.last_updated} />

        <GuideArticleWrapper>
          <CoinzillaHorizontal />
          <div
            itemScope
            itemProp="itemListElement"
            itemType="https://schema.org/HowToDirection"
          >
            {t("intro", { name: post.name })}
          </div>
          <div
            itemScope
            itemProp="step"
            itemType="https://schema.org/HowToStep"
          >
            <h2>
              <StyledCounter>1</StyledCounter>
              {t("step1Title", { exchange: exchangeTxt })}
            </h2>

            <div
              itemScope
              itemProp="itemListElement"
              itemType="https://schema.org/HowToDirection"
            >
              {t("step1Content", { exchange: exchangeTxt, name: post.name })}
            </div>
          </div>

          <CtaLink
            color={theme.accent_3}
            to={chosenExchange}
            text={t(`step1CTA`, { exchange: exchangeTxt })}
            inline
          />

          <div
            itemScope
            itemProp="step"
            itemType="https://schema.org/HowToStep"
          >
            <h2>
              <StyledCounter>2</StyledCounter>
              {t("step2Title")}
            </h2>
            <div
              itemScope
              itemProp="itemListElement"
              itemType="https://schema.org/HowToDirection"
            >
              {step2Sequence.map((step) => {
                return (
                  <ReactMarkdown
                    remarkPlugins={[remarkUnlink]}
                    components={elRenderer}
                    rehypePlugins={[rehypeRaw]}
                    key={step}
                  >
                    {step}
                  </ReactMarkdown>
                );
              })}
            </div>
          </div>
          <div
            itemScope
            itemProp="step"
            itemType="https://schema.org/HowToStep"
          >
            <h2>
              <StyledCounter>3</StyledCounter>
              {t("step3Title", { name: post.name })}
            </h2>

            <div
              itemScope
              itemProp="itemListElement"
              itemType="https://schema.org/HowToDirection"
            >
              <ReactMarkdown
                remarkPlugins={[remarkUnlink]}
                components={elRenderer}
                rehypePlugins={[rehypeRaw]}
              >
                {t("step3Content", { name: post.name })}
              </ReactMarkdown>
            </div>
            <CtaLink
              inline
              color={theme.accent_3}
              text={t("ledgerCta")}
              to={`ledger`}
              source={post.slug}
              content={`guide_wallet`}
            />
          </div>

          <CoinzillaHorizontal />
        </GuideArticleWrapper>
      </article>
    </Layout>
  );
}

export async function getStaticProps(context) {
  const userAgentString = `Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/100.0.4896.127 Safari/537.36`;
  const qs = require("qs");
  const fetcher = require("@/utils/utils-cjs").fetcher;

  const {
    params: { slug },
    locale,
  } = context;

  const walletQuery = qs.stringify({
    filters: {
      type: {
        $eq: "wallet",
      },
    },
    populate: {
      attachments: {
        fields: ["url"],
      },
    },
  });

  // const walletRes = await fetcher(`/api/affiliates?${walletQuery}`);
  // const walletData = await walletRes.json();
  // const parsedWalletData = walletData.data.map((wallet) => {
  //   return wallet.attributes;
  // });
  const query = qs.stringify(
    {
      filters: {
        url_slug: {
          $eq: `/guides/${slug}`,
        },
      },
      fields: [
        "cmc_id",
        "last_updated",
        "name",
        "symbol",
        "market_pairs",
        "slug",
      ],
    },
    { sort: (a, b) => a.localeCompare(b) }
  );
  const res = await fetcher(`/api/coins?${query}`);
  const data = await res.json();

  if (!data) {
    return {
      notFound: true,
    };
  }

  if (data.data.length >= 1) {
    const i18nConfig = require("../../next-i18next.config");
    return {
      props: {
        data: {
          ...data.data[0],
        },
        // walletData: parsedWalletData,
        ...(await serverSideTranslations(locale, ["newguide"], i18nConfig)),
      },
      // let's make this for as long as possible
      revalidate: 60 * 60 * 24 * 7,
    };
  } else {
    return {
      notFound: true,
    };
  }
}

export async function getStaticPaths() {
  const qs = require("qs");
  const fetcher = require("@/utils/utils-cjs").fetcher;

  const query = qs.stringify({
    pagination: {
      page: 1,
      pageSize: 10,
    },
    fields: ["url_slug"],
  });
  const res = await fetcher(`/api/coins?${query}`);
  const data = await res.json();

  const paths = data.data.map((item) => ({
    params: {
      slug: item.attributes.url_slug,
    },
  }));
  return { paths, fallback: "blocking" };
}
